import React, { useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  Box, Button, Chip, CircularProgress, IconButton, InputAdornment, Stack,
  Table, TableBody, TableCell, TableHead, TablePagination, TableRow, TextField, Typography,
  useMediaQuery
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  Add as AddIcon, Search as SearchIcon, EditOutlined as EditIcon, DeleteOutline as DeleteIcon,
  ContentCopy as CopyIcon, ChevronRight as ChevronRightIcon
} from '@mui/icons-material';
import ConfirmDeleteModal from 'components/shared/modals/ConfirmDeleteModal';
import OrganizationForm from 'components/admin-organizations/OrganizationForm';
import { ORGANIZATIONS, DELETE_ORGANIZATIONS } from 'api/graphql/organization';
import routes from 'common/routes';

const PER_PAGE = 20;

function OrganizationList() {
  const theme = useTheme();
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { t } = useTranslation(['organizations']);

  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [form, setForm] = useState({ open: false, organization: null });
  const [del, setDel] = useState({ open: false, id: null });

  const { data, loading, refetch } = useQuery(ORGANIZATIONS, {
    variables: { first: PER_PAGE, page: page + 1, search },
    fetchPolicy: 'cache-and-network'
  });
  const [deleteOrganizations] = useMutation(DELETE_ORGANIZATIONS);

  const rows = data?.organizations?.data ?? [];
  const total = data?.organizations?.paginatorInfo?.total ?? 0;

  const confirmDelete = async () => {
    if (del.id) await deleteOrganizations({ variables: { id: [del.id] } });
    setDel({ open: false, id: null });
    refetch();
  };

  const copyCode = (code) => { navigator.clipboard?.writeText(code).catch(() => {}); };
  const goToDetail = (id) => navigate(routes.adminOrganizationDetail.path.replace(':id', id));

  const activeChip = (isActive) => (
    <Chip
      size="small"
      label={isActive ? t('active', { defaultValue: 'Activa' }) : t('inactive', { defaultValue: 'Inactiva' })}
      sx={{ backgroundColor: isActive ? 'rgba(27,147,75,0.16)' : 'rgba(128,128,128,0.18)', color: isActive ? '#5FE0A0' : 'text.secondary', fontWeight: 700 }}
    />
  );

  return (
    <Box>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 2 }} alignItems={{ sm: 'center' }}>
        <TextField
          size="small"
          placeholder={t('searchPlaceholder', { defaultValue: 'Buscar por nombre o código' })}
          value={search}
          onChange={(e) => { setSearch(e.target.value); setPage(0); }}
          fullWidth
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 18 }} /></InputAdornment> }}
        />
        <Button variant="contained" color="primary" startIcon={<AddIcon />} onClick={() => setForm({ open: true, organization: null })} sx={{ textTransform: 'none', fontWeight: 700, whiteSpace: 'nowrap' }}>
          {t('newOrganization', { defaultValue: 'Nueva organización' })}
        </Button>
      </Stack>

      <Box sx={{ borderRadius: 2, overflow: 'hidden', border: '1px solid', borderColor: 'hairline.main', backgroundColor: 'surface.content' }}>
        {loading && rows.length === 0 && (
          <Box sx={{ py: 4, textAlign: 'center' }}><CircularProgress size={24} sx={{ color: 'gold.main' }} /></Box>
        )}
        {!loading && rows.length === 0 && (
          <Box sx={{ py: 4, textAlign: 'center', color: 'text.secondary' }}>{t('empty', { defaultValue: 'No hay organizaciones.' })}</Box>
        )}

        {rows.length > 0 && (isMobile ? (
          <Box>
            {rows.map((o) => (
              <Stack key={o.id} direction="row" alignItems="center" spacing={1} sx={{ px: 1.5, py: 1.25, borderBottom: (th) => `2px solid ${th.palette.stackedRowBorder}`, '&:last-of-type': { borderBottom: 0 } }}>
                <Box sx={{ flex: 1, minWidth: 0 }} onClick={() => goToDetail(o.id)} role="button" tabIndex={0}>
                  <Typography variant="body2" sx={{ fontWeight: 800 }} noWrap>{o.name}</Typography>
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>{o.join_code} · {o.members_count} {t('members', { defaultValue: 'miembros' })}</Typography>
                </Box>
                {activeChip(o.is_active)}
                {!o.is_default && (
                  <IconButton size="small" onClick={() => setDel({ open: true, id: o.id })} sx={{ color: (th) => th.palette.deleteAction.fg }}><DeleteIcon fontSize="small" /></IconButton>
                )}
                <IconButton size="small" onClick={() => goToDetail(o.id)}><ChevronRightIcon fontSize="small" /></IconButton>
              </Stack>
            ))}
          </Box>
        ) : (
          <Box sx={{ overflowX: 'auto' }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>{t('name', { defaultValue: 'Nombre' })}</TableCell>
                  <TableCell>{t('code', { defaultValue: 'Código' })}</TableCell>
                  <TableCell align="center">{t('members', { defaultValue: 'Miembros' })}</TableCell>
                  <TableCell align="center">{t('status', { defaultValue: 'Estado' })}</TableCell>
                  <TableCell align="right">{t('actions', { defaultValue: 'Acciones' })}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((o) => (
                  <TableRow key={o.id} hover sx={{ cursor: 'pointer' }} onClick={() => goToDetail(o.id)}>
                    <TableCell sx={{ fontWeight: 800 }}>{o.name}</TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={0.5} alignItems="center">
                        <Typography variant="body2" sx={{ fontFamily: 'monospace', letterSpacing: 1 }}>{o.join_code}</Typography>
                        <IconButton size="small" onClick={(e) => { e.stopPropagation(); copyCode(o.join_code); }}><CopyIcon sx={{ fontSize: 15 }} /></IconButton>
                      </Stack>
                    </TableCell>
                    <TableCell align="center">{o.members_count}</TableCell>
                    <TableCell align="center">{activeChip(o.is_active)}</TableCell>
                    <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                      <IconButton size="small" onClick={() => setForm({ open: true, organization: o })}><EditIcon fontSize="small" /></IconButton>
                      {!o.is_default && (
                        <IconButton size="small" onClick={() => setDel({ open: true, id: o.id })} sx={{ color: (th) => th.palette.deleteAction.fg }}><DeleteIcon fontSize="small" /></IconButton>
                      )}
                      <IconButton size="small" onClick={() => goToDetail(o.id)}><ChevronRightIcon fontSize="small" /></IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        ))}

        <TablePagination component="div" count={total} page={page} onPageChange={(_, p) => setPage(p)} rowsPerPage={PER_PAGE} rowsPerPageOptions={[PER_PAGE]} />
      </Box>

      {form.open && <OrganizationForm open={form.open} organization={form.organization} onClose={() => { setForm({ open: false, organization: null }); refetch(); }} />}
      <ConfirmDeleteModal open={del.open} onClose={() => setDel({ open: false, id: null })} onConfirm={confirmDelete} count={1} />
    </Box>
  );
}

export default OrganizationList;
